import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellRingIcon, MessageSquareTextIcon, CalendarCheckIcon } from 'lucide-react';
type Phase = 'entering' | 'holding' | 'exiting' | 'waiting';
export function LeadFlowAnimation() {
  const [phase, setPhase] = useState<Phase>('waiting');
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    // Small initial delay then start
    const startDelay = setTimeout(() => setPhase('entering'), 400);
    return () => clearTimeout(startDelay);
  }, []);
  useEffect(() => {
    if (phase === 'entering') {
      // After all 3 steps have entered (step3 enters at ~5s), hold for 3s
      const holdTimer = setTimeout(() => setPhase('holding'), 5800);
      return () => clearTimeout(holdTimer);
    }
    if (phase === 'holding') {
      const exitTimer = setTimeout(() => setPhase('exiting'), 3000);
      return () => clearTimeout(exitTimer);
    }
    if (phase === 'exiting') {
      const resetTimer = setTimeout(() => {
        setPhase('waiting');
        setCycle(c => c + 1);
      }, 1000);
      return () => clearTimeout(resetTimer);
    }
    if (phase === 'waiting') {
      const restartTimer = setTimeout(() => setPhase('entering'), 800);
      return () => clearTimeout(restartTimer);
    }
  }, [phase]);
  const isVisible = phase === 'entering' || phase === 'holding';
  const stepVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.97
    }
  };
  const connectorVariants = {
    hidden: {
      scaleY: 0,
      opacity: 0
    },
    visible: {
      scaleY: 1,
      opacity: 1
    },
    exit: {
      scaleY: 0,
      opacity: 0
    }
  };
  return <div className="relative w-full max-w-sm mx-auto" data-id="element-203">
      <AnimatePresence mode="wait" data-id="element-204">
        {(isVisible || phase === 'exiting') && <motion.div key={cycle} className="flex flex-col items-center gap-0" initial="hidden" animate={phase === 'exiting' ? 'exit' : 'visible'} exit="exit" data-id="element-205">
            {/* Step 1: New Lead */}
            <motion.div variants={stepVariants} transition={{
          duration: 0.5,
          delay: phase === 'exiting' ? 0 : 0
        }} className="w-full" data-id="element-206">
              <div className="bg-viridia-surface border border-gray-800 rounded-xl p-4 shadow-lg" data-id="element-207">
                <div className="flex items-center gap-3" data-id="element-208">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-209">
                    <BellRingIcon className="w-4.5 h-4.5 text-amber-400" data-id="element-210" />
                  </div>
                  <div className="min-w-0" data-id="element-211">
                    <p className="text-white text-sm font-semibold" data-id="element-212">New Lead</p>
                    <p className="text-gray-500 text-xs" data-id="element-213">From Google Ads</p>
                  </div>
                  <span className="ml-auto text-xs text-gray-600 flex-shrink-0" data-id="element-214">
                    Just now
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Connector 1 */}
            <motion.div variants={connectorVariants} transition={{
          duration: 0.5,
          delay: phase === 'exiting' ? 0 : 1.2
        }} className="flex flex-col items-center py-1 origin-top" data-id="element-215">
              <div className="w-px h-3 bg-gray-700" data-id="element-216"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-viridia/50 my-0.5" data-id="element-217"></div>
              <div className="w-px h-3 bg-gray-700" data-id="element-218"></div>
            </motion.div>

            {/* Step 2: Automated Response */}
            <motion.div variants={stepVariants} transition={{
          duration: 0.6,
          delay: phase === 'exiting' ? 0.05 : 2.0
        }} className="w-full" data-id="element-219">
              <div className="bg-viridia-surface border border-gray-800 rounded-xl p-4 shadow-lg" data-id="element-220">
                <div className="flex items-start gap-3" data-id="element-221">
                  <div className="w-9 h-9 rounded-lg bg-viridia/15 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-222">
                    <MessageSquareTextIcon className="w-4.5 h-4.5 text-viridia" data-id="element-223" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-224">
                    <p className="text-white text-sm font-semibold mb-1.5" data-id="element-225">
                      Auto Response Sent
                    </p>
                    <div className="bg-viridia-black/80 rounded-lg p-2.5 border border-gray-800/50" data-id="element-226">
                      <p className="text-gray-300 text-xs leading-relaxed" data-id="element-227">
                        "Hi! Ready to book your free estimate? Pick a time that
                        works:"
                      </p>
                      <div className="mt-2 inline-flex items-center gap-1.5 bg-viridia/10 border border-viridia/20 rounded-md px-2.5 py-1" data-id="element-228">
                        <CalendarCheckIcon className="w-3 h-3 text-viridia" data-id="element-229" />
                        <span className="text-viridia text-xs font-medium" data-id="element-230">
                          Book Now
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Connector 2 */}
            <motion.div variants={connectorVariants} transition={{
          duration: 0.5,
          delay: phase === 'exiting' ? 0 : 3.6
        }} className="flex flex-col items-center py-1 origin-top" data-id="element-231">
              <div className="w-px h-3 bg-gray-700" data-id="element-232"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-viridia/50 my-0.5" data-id="element-233"></div>
              <div className="w-px h-3 bg-gray-700" data-id="element-234"></div>
            </motion.div>

            {/* Step 3: Estimate Booked */}
            <motion.div variants={stepVariants} transition={{
          duration: 0.6,
          delay: phase === 'exiting' ? 0.1 : 4.6
        }} className="w-full" data-id="element-235">
              <div className="bg-viridia-surface border border-viridia/30 rounded-xl p-4 shadow-[0_0_20px_rgba(16,185,129,0.08)]" data-id="element-236">
                <div className="flex items-center gap-3" data-id="element-237">
                  <div className="w-9 h-9 rounded-lg bg-viridia/20 flex items-center justify-center flex-shrink-0" data-id="element-238">
                    <CalendarCheckIcon className="w-4.5 h-4.5 text-viridia" data-id="element-239" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-240">
                    <div className="flex items-center gap-2" data-id="element-241">
                      <p className="text-viridia text-sm font-bold" data-id="element-242">
                        Estimate Booked
                      </p>
                      <span className="text-viridia text-sm" data-id="element-243">✓</span>
                    </div>
                    <p className="text-gray-500 text-xs" data-id="element-244">
                      HVAC Repair · Tomorrow, 2:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}